import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import ReviewsSection from './components/ReviewsSection';
import ReviewForm from './components/ReviewForm';
import { workerProfilesService } from '../../services/supabaseService';
import { reviewsService } from '../../services/reviewsService';
import { supabase } from '../../lib/supabase';

const UserProfile = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams?.get('userId');
  
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchProfileData();
    }
  }, [userId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase?.auth?.getUser();
      setCurrentUser(user);

      const [profileData, reviewsData] = await Promise.all([
        workerProfilesService?.getWorkerProfile(userId),
        reviewsService?.getUserReviews(userId)
      ]);

      setProfile(profileData);
      setReviews(reviewsData || []);
    } catch (err) {
      console.error('Error fetching profile data:', err);
      setError(err?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmitted = async () => {
    setShowReviewForm(false);
    await fetchProfileData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)] space-y-4">
          <Icon name="AlertCircle" className="w-12 h-12 text-error" />
          <p className="text-lg text-muted-foreground">{error || 'Profile not found'}</p>
        </div>
      </div>
    );
  }

  const canLeaveReview = currentUser && currentUser?.id !== userId;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{profile?.userProfiles?.fullName || 'User Profile'} - Profile</title>
      </Helmet>
      
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Profile Header */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                {profile?.userProfiles?.avatarUrl ? (
                  <img 
                    src={profile?.userProfiles?.avatarUrl} 
                    alt={profile?.userProfiles?.fullName || 'User avatar'}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <Icon name="User" className="w-12 h-12 text-primary" />
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-1">
                    {profile?.userProfiles?.fullName || 'User'}
                  </h1>
                  <p className="text-muted-foreground">{profile?.userProfiles?.email}</p>
                </div>
                {profile?.isVerified && (
                  <div className="flex items-center gap-1 bg-success/10 text-success px-3 py-1 rounded-full text-sm">
                    <Icon name="CheckCircle" className="w-4 h-4" />
                    <span>Verified</span>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Icon name="Star" className="w-5 h-5 text-warning" />
                    <span className="text-xl font-bold text-foreground">
                      {profile?.rating?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Rating</p>
                </div>
                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="text-xl font-bold text-foreground mb-1">
                    {profile?.totalReviews || 0}
                  </div>
                  <p className="text-xs text-muted-foreground">Reviews</p>
                </div>
                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="text-xl font-bold text-foreground mb-1">
                    {profile?.totalJobsCompleted || 0}
                  </div>
                  <p className="text-xs text-muted-foreground">Jobs Done</p>
                </div>
                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="text-xl font-bold text-foreground mb-1">
                    {profile?.experienceYears || 0}y
                  </div>
                  <p className="text-xs text-muted-foreground">Experience</p>
                </div>
              </div>

              {/* Bio */}
              {profile?.bio && (
                <p className="text-foreground mb-4">{profile?.bio}</p>
              )}

              {/* Skills */}
              {profile?.skills && profile?.skills?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profile?.skills?.map((skill, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">Reviews & Ratings</h2>
            {canLeaveReview && (
              <button
                onClick={() => setShowReviewForm(!showReviewForm)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" className="w-4 h-4" />
                <span>Write Review</span>
              </button>
            )}
          </div>

          {showReviewForm && (
            <div className="mb-6">
              <ReviewForm
                toUserId={userId}
                onSuccess={handleReviewSubmitted}
                onCancel={() => setShowReviewForm(false)}
              />
            </div>
          )}

          <ReviewsSection
            reviews={reviews}
            currentUserId={currentUser?.id}
            onReviewUpdated={fetchProfileData}
          />
        </div>
      </div>
    </div>
  );
};

export default UserProfile;